import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Building2, CalendarX, FileText, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const APP_STATUS = {
  completata: { label: "Completata", cls: "bg-green-100 text-green-800" },
  non_completata: { label: "Non completata", cls: "bg-red-100 text-red-800" },
  annullata: { label: "Annullata", cls: "bg-red-100 text-red-800" },
};
const APP_STATUS_DEFAULT = { label: "Da completare", cls: "bg-amber-100 text-amber-800" };

export default function TenantLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [courses, setCourses] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [myApps, setMyApps] = useState(null);

  useEffect(() => {
    if (user && user.role === "candidate") {
      api.get("/applications/mine").then(({ data }) => setMyApps(data)).catch(() => setMyApps([]));
    }
  }, [user]);

  useEffect(() => {
    api.get(`/tenants/slug/${slug}`)
      .then(({ data }) => {
        setTenant(data);
        if (data.active && data.applications_open) {
          localStorage.setItem("its_tenant_slug", data.slug);
          api.get(`/courses?tenant_id=${data.id}`).then(({ data: c }) => setCourses(c)).catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="tenant-not-found">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-slate-900">Istituto non trovato</h1>
          <p className="mt-2 text-sm text-slate-600">Verifica l'indirizzo della pagina di selezione.</p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="tenant-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#003366]" />
      </div>
    );
  }

  if (!tenant.active || !tenant.applications_open) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center fade-up" data-testid="tenant-closed-page">
          <CalendarX className="h-14 w-14 text-slate-300 mx-auto mb-5" />
          <p className="eyebrow mb-3">{tenant.name}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Selezione non ancora aperta
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-md mx-auto">
            Le candidature per questo istituto non sono attualmente aperte. Torna a visitare questa pagina:
            il bando di selezione sarà pubblicato qui.
          </p>
        </div>
      </div>
    );
  }

  const hasAppHere = (myApps || []).some((a) => a.tenant_id === tenant.id);
  const otherApps = (myApps || []).filter((a) => a.tenant_id !== tenant.id);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-3xl fade-up">
          <p className="eyebrow mb-3">{tenant.name}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Bando di selezione
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            Sono aperte le candidature ai percorsi post-diploma di {tenant.name}. Presenta la domanda online,
            sostieni la prova di selezione e completa il percorso con il colloquio motivazionale.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {hasAppHere ? (
              <Button data-testid="cta-goto-application" size="lg" className="bg-[#003366] hover:bg-[#002244]" onClick={() => navigate("/dashboard")}>
                Vai alla tua candidatura <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button data-testid="cta-apply-now" size="lg" className="bg-[#003366] hover:bg-[#002244]" onClick={() => navigate(user ? "/candidatura" : "/auth")}>
                Inizia la candidatura per questo istituto <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            <Button data-testid="cta-courses" size="lg" variant="outline"
              onClick={() => document.getElementById("corsi")?.scrollIntoView({ behavior: "smooth" })}>
              Scopri i corsi
            </Button>
          </div>
        </div>
      </section>

      {user && user.role === "candidate" && otherApps.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 -mt-6" data-testid="tenant-my-applications">
          <Card className="institutional-card">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-[#003366]" />
                <h2 className="font-display text-lg font-semibold text-slate-900">Le tue candidature presso altri istituti</h2>
              </div>
              <ul className="divide-y divide-slate-100">
                {otherApps.map((a) => {
                  const st = APP_STATUS[a.status] || APP_STATUS_DEFAULT;
                  return (
                    <li key={a.id} data-testid={`tenant-my-app-${a.id}`} className="py-3 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{a.tenant_name}</p>
                        <p className="text-sm text-slate-500">{a.course_name}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={st.cls}>{st.label}</Badge>
                        <Button data-testid={`tenant-my-app-open-${a.id}`} variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
                          Apri area personale
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        </section>
      )}

      <section id="corsi" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="eyebrow mb-3">Offerta formativa</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Corsi attivi</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="tenant-courses-list">
            {courses.map((c) => (
              <Card key={c.id} data-testid={`course-card-${c.id}`} className="institutional-card hover:shadow-md transition-shadow">
                <CardContent className="p-6 sm:p-8">
                  <div className="proctor-badge text-[#0284C7] mb-3">{c.duration}</div>
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900">{c.name}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-normal">{c.description}</p>
                </CardContent>
              </Card>
            ))}
            {courses.length === 0 && <p className="text-slate-500 text-sm">Caricamento corsi...</p>}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="institutional-card rounded-2xl border border-slate-200 bg-white p-8 sm:p-12 flex flex-col sm:flex-row items-start gap-6 fade-up" data-testid="colloquio-section">
          <div className="h-12 w-12 shrink-0 rounded-xl bg-[#E0E7FF] text-[#003366] flex items-center justify-center">
            <Users className="h-6 w-6" />
          </div>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Completa la candidatura con il colloquio
            </h2>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              Colloquio motivazionale in presenza, convocazione entro il mese di settembre.
            </p>
          </div>
        </div>
      </section>

      <footer className="bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="font-display font-bold">{tenant.name}</div>
          <p className="text-sm text-blue-200">Selezione gestita con ITS Match</p>
        </div>
      </footer>
    </div>
  );
}
